import { useEffect, useState, type FormEvent } from "react";
import { getStaff, send } from "../api.js";
import { useAsync } from "../useAsync.js";
import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";

export interface TeacherPaymentTracker {
  tutor_profile_id: string;
  tutor_name: string;
  amelia_provider_id: number | null;
  instruments: string[];
  relationship: "current" | "former" | "tracker_only";
  authoritative_from: string | null;
  effective_from: string;
  can_edit: boolean;
}

export interface TeacherPaymentTrackingRead {
  payment_tracking_authoritative_from: string | null;
  default_authoritative_from: string;
  can_edit_student_default: boolean;
  trackers: TeacherPaymentTracker[];
}

function readable(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
  });
}

function trackerLabel(tracker: TeacherPaymentTracker): string {
  const instruments = tracker.instruments.length ? ` — ${tracker.instruments.join(", ")}` : "";
  const suffix = tracker.amelia_provider_id === null
    ? " (no booking identity)"
    : tracker.relationship === "former"
      ? " (former teacher)"
      : tracker.relationship === "tracker_only" ? " (historical tracker)" : "";
  return `${tracker.tutor_name}${instruments}${suffix}`;
}

/** Staff can inspect every teacher's boundary; only the owner can edit another tutor's. */
export function TeacherPaymentTracking({
  studentId, data, reload, owner, viewerId,
}: {
  studentId: string;
  data: TeacherPaymentTrackingRead;
  reload: () => void;
  owner: boolean;
  viewerId: string;
}) {
  const publish = usePublishActionOutcome();
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const storageKey = `wmf.teacher-payment-tracker.${viewerId}.${studentId}`;
  const [selectedId, setSelectedId] = useState(() => {
    try { return window.localStorage.getItem(storageKey) ?? ""; }
    catch { return ""; }
  });
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newTeacher, setNewTeacher] = useState("");
  const [newDate, setNewDate] = useState("");
  const staff = useAsync<Awaited<ReturnType<typeof getStaff>> | null>(
    async () => owner ? getStaff() : null,
    [owner],
  );
  const tracker = data.trackers.find(row => row.tutor_profile_id === selectedId);
  const selected = tracker ?? null;
  const availableTutors = (staff.data?.tutors ?? []).filter(tutor =>
    tutor.tutorProfileId && tutor.externalProviderId !== null &&
    tutor.state !== "left" &&
    !data.trackers.some(row => row.tutor_profile_id === tutor.tutorProfileId),
  );

  useEffect(() => {
    // An inherited date must stay blank: copying the default severs inheritance.
    if (!editing) setValue(selected ? (selected.authoritative_from ?? "") : (data.payment_tracking_authoritative_from ?? ""));
  }, [editing, selected?.authoritative_from, data.payment_tracking_authoritative_from]);

  function choose(value: string) {
    setSelectedId(value);
    setEditing(false);
    setProblem(null);
    try { window.localStorage.setItem(storageKey, value); }
    catch { /* Private browsing and storage-disabled browsers keep working. */ }
  }

  async function save(event?: FormEvent, override?: string | null) {
    event?.preventDefault();
    if (saving) return;
    setSaving(true);
    setProblem(null);
    try {
      const date = override === undefined ? value || null : override;
      if (selected) {
        await send("PUT",
          `/admin/students/${studentId}/payment-tracking/teachers/${selected.tutor_profile_id}`,
          { authoritative_from: date });
      } else {
        await send("PUT", `/admin/students/${studentId}/payment-tracking`,
          { payment_tracking_authoritative_from: date });
      }
      const unchanged = !selected
        ? data.trackers.filter(row => row.authoritative_from !== null).length
        : 0;
      const result: ActionOutcome = {
        variant: "success",
        title: selected
          ? `Payment tracking date saved for ${selected.tutor_name}`
          : "Student payment tracking date saved",
        details: [
          { value: date ? readable(date) : selected ? "Using student date" : `Using ${readable(data.default_authoritative_from)}` },
          ...(unchanged ? [{ value: `${unchanged} teacher${unchanged === 1 ? "" : "s"} with their own dates were not changed` }] : []),
        ],
      };
      if (!publish(result)) setOutcome(result);
      setEditing(false);
      await reload();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not save payment tracking date.");
    } finally {
      setSaving(false);
    }
  }

  async function addTracker(event: FormEvent) {
    event.preventDefault();
    if (saving || !newTeacher || !newDate) return;
    setSaving(true);
    setProblem(null);
    try {
      await send("POST", `/admin/students/${studentId}/payment-tracking/teachers`,
        { tutor_profile_id: newTeacher, authoritative_from: newDate });
      const name = availableTutors.find(tutor => tutor.tutorProfileId === newTeacher)?.displayName ?? "teacher";
      const result: ActionOutcome = { variant: "success", title: `Payment tracker created for ${name}` };
      if (!publish(result)) setOutcome(result);
      setAdding(false);
      choose(newTeacher);
      setNewTeacher("");
      setNewDate("");
      await reload();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not add teacher tracker.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel" style={{ marginTop: 16 }} aria-label="Payment tracking">
      {outcome && <ActionConfirmation {...outcome} />}
      <strong>Payment tracking</strong>
      <p className="small muted">
        Credits automatically settle the oldest eligible unpaid lessons. Older legacy lessons
        can be protected with a later date. Each teacher can have their own date; a teacher
        without one uses the student's date.
      </p>

      <label>
        <span className="small muted">Tracker</span>
        <select value={selected?.tutor_profile_id ?? ""} onChange={event => choose(event.target.value)}>
          <option value="">All teachers (student default)</option>
          {data.trackers.map(row => (
            <option key={row.tutor_profile_id} value={row.tutor_profile_id}
              disabled={row.amelia_provider_id === null && row.authoritative_from === null}>{trackerLabel(row)}</option>
          ))}
        </select>
      </label>

      <div className="row" style={{ justifyContent: "space-between", marginTop: 12 }}>
        <p style={{ margin: 0 }}>
          <span className="small muted">{selected?.authoritative_from ? "Authoritative from" :
            selected ? "Inherits student date" : "Authoritative from"}</span><br />
          {selected
            ? <>{readable(selected.effective_from)} {selected.authoritative_from ? "(this teacher)" : "(student date)"}</>
            : data.payment_tracking_authoritative_from
              ? readable(data.payment_tracking_authoritative_from)
              : <span className="muted">Automatic — all available history (from {readable(data.default_authoritative_from)})</span>}
        </p>
        {!editing && (selected ? selected.can_edit : data.can_edit_student_default) && (
          <button className="quiet small" type="button" onClick={() => setEditing(true)}>Edit</button>
        )}
      </div>

      {editing && (
        <form onSubmit={event => { void save(event); }} style={{ marginTop: 12 }}>
          <label>
            <span className="small muted">Authoritative from</span>
            <input type="date" value={value} onChange={event => setValue(event.target.value)} />
          </label>
          <p className="small muted">
            {selected ? "Clear the override to inherit the student's payment history date." :
              `Leave blank to use all available history from ${readable(data.default_authoritative_from)}. Teacher overrides will not change.`}
          </p>
          <div className="row">
            {selected
              ? <button className="quiet" disabled={saving} type="button" onClick={() => { void save(undefined, null); }}>Use student date</button>
              : <button className="quiet" disabled={saving} type="button" onClick={() => setValue(data.default_authoritative_from)}>Use all available history</button>}
            <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="quiet" type="button" disabled={saving} onClick={() => { setEditing(false); setProblem(null); }}>Cancel</button>
          </div>
        </form>
      )}
      {problem && <div className="error small" role="alert">{problem}</div>}

      {!!data.trackers.length && (
        <div style={{ overflowX: "auto", marginTop: 16 }}>
          <strong className="small">Teacher payment history</strong>
          <table>
            <thead><tr><th>Teacher</th><th>Instrument</th><th>Effective from</th><th>Source</th></tr></thead>
            <tbody>
              {data.trackers.map(row => (
                <tr key={row.tutor_profile_id}>
                  <td>
                    <button className="quiet small" type="button" disabled={row.amelia_provider_id === null && row.authoritative_from === null}
                      onClick={() => choose(row.tutor_profile_id)}>
                      {row.tutor_name}
                    </button>
                    {row.relationship !== "current" && <span className="small muted"> ({row.relationship === "former" ? "former" : "historical"})</span>}
                    {row.amelia_provider_id === null && <span className="small muted"> — no booking identity{row.authoritative_from !== null ? "; existing override can be cleared" : ""}</span>}
                  </td>
                  <td>{row.instruments.join(", ") || "—"}</td>
                  <td>{readable(row.effective_from)}</td>
                  <td>{row.authoritative_from ? "Own date" : "Inherited"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {owner && (
        <div style={{ marginTop: 12 }}>
          {!adding && <button className="quiet small" type="button" onClick={() => { setAdding(true); setProblem(null); }}>Add tracker for another teacher</button>}
          {adding && <form onSubmit={addTracker}>
            <label><span className="small muted">Teacher</span>
              <select required value={newTeacher} onChange={event => setNewTeacher(event.target.value)}>
                <option value="">Choose a teacher…</option>
                {availableTutors.map(tutor => <option key={tutor.tutorProfileId!} value={tutor.tutorProfileId!}>{tutor.displayName}</option>)}
              </select>
            </label>
            <label><span className="small muted">Authoritative from</span>
              <input required type="date" value={newDate} onChange={event => setNewDate(event.target.value)} />
            </label>
            {staff.error && <div className="error small">{staff.error}</div>}
            <div className="row"><button type="submit" disabled={saving || !newTeacher || !newDate}>Save tracker</button>
              <button className="quiet" type="button" disabled={saving} onClick={() => setAdding(false)}>Cancel</button></div>
          </form>}
        </div>
      )}
    </section>
  );
}
